import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import React, { ReactElement, useContext, useState } from "react";

import CollectionModal from "../../components/collection-modal/collection-modal";
import InputText from "../../components/text/input-text";
import {
  AssetCategoryIdFilter,
  AssetMetashotsType,
  AssetStatus,
  AssetTypes,
  Category,
  LanguageRegion,
  Sort,
} from "../../generated/gql/graphql";
import { AssetListContext } from "../../providers/asset-list-provider";
import { FilesContext } from "../../providers/files-provider";
import AssetSelectionActions from "../asset/asset-selection-actions";

import AssetStatusFilter from "./filters/asset-status-filter";
import AssetTypeFilter from "./filters/asset-type-filter";
import CategoryFilter from "./filters/category-filter";
import LanguageRegionFilter from "./filters/language-region-filter";
import MetashotTypeFilter, { styles } from "./filters/metashot-type-filter";

export default function FilesFilters(): ReactElement {
  const [openDialog, setOpenDialog] = React.useState(false);

  const { selectedFiles, selectedAllAssets, selectAll, clearSelection } =
    useContext(AssetListContext);
  const { filter, setFilter, orderBy, setOrderBy } = useContext(FilesContext);
  const [internalOrderState, setInternalOrderState] = useState<string>();

  // Todo: Preload language based on user account

  function handleSortChange(e: SelectChangeEvent): void {
    switch (e.target.value) {
      case "name": {
        setOrderBy({
          name: Sort.Asc,
        });
        break;
      }
      case "-name": {
        setOrderBy({
          name: Sort.Desc,
        });
        break;
      }
      case "date_created": {
        setOrderBy({
          date_created: Sort.Desc,
        });
        break;
      }
      case "-date_created": {
        setOrderBy({
          date_created: Sort.Asc,
        });
        break;
      }
    }

    setInternalOrderState(e.target.value);
  }

  function onRegionLanguageSelected(lang: LanguageRegion | "all"): void {
    setFilter({
      ...filter,
      languageRegionId:
        lang === "all"
          ? undefined
          : {
              eq: lang.id,
            },
    });
  }

  function onCategorySelected(category: Category | "all"): void {
    setFilter({
      ...filter,
      categoryId:
        category === "all"
          ? undefined
          : {
              eq: category.id,
            },
    });
  }

  function onMetashotSelected(metashotType: AssetMetashotsType | "all"): void {
    setFilter({
      ...filter,
      metashotsType:
        metashotType === "all"
          ? undefined
          : {
              eq: metashotType,
            },
    });
  }

  function onAssetStatusSelected(status: AssetStatus | "all"): void {
    setFilter({
      ...filter,
      status:
        status === "all"
          ? undefined
          : {
              eq: status,
            },
    });
  }

  function onAssetTypeSelected(assetTypes: AssetTypes | "all"): void {
    setFilter({
      ...filter,
      assetTypes:
        assetTypes === "all"
          ? undefined
          : {
              eq: assetTypes,
            },
    });
  }

  function onAssetMasterIdSelected(value: string): void {
    setFilter({
      ...filter,
      masterId: value.length
        ? {
            eq: value,
          }
        : undefined,
    });
  }

  function onAssetProjectIdSelected(value: string): void {
    setFilter({
      ...filter,
      projectId: value.length
        ? {
            eq: value,
          }
        : undefined,
    });
  }

  function onAssetAdaptionIdSelected(value: string): void {
    setFilter({
      ...filter,
      adoptionId: value.length
        ? {
            eq: value,
          }
        : undefined,
    });
  }

  function onResetFilters(): void {
    setFilter({});
    setOrderBy({
      date_created: Sort.Desc,
    });
  }

  return (
    <div className="flex w-full mb-[25px] flex-wrap">
      <div className="block basis-[60%]">
        <div className="flex gap-[15px] flex-wrap">
          <AssetTypeFilter
            value={filter?.assetTypes?.eq as AssetTypes | undefined}
            onSelect={onAssetTypeSelected}
          />
          <AssetStatusFilter
            value={filter?.status?.eq as AssetStatus | undefined}
            onSelect={onAssetStatusSelected}
          />

          {/* <UISelect className="mr-15" label="Creator" name="user_created" list={users} /> */}
          {/* <UISelect className="mr-15" label="File Type" name="file_type" list={getListType(fields, 'file_type')} /> */}

          <MetashotTypeFilter
            className="w-[150px]"
            value={filter?.metashotsType as AssetMetashotsType | undefined}
            onSelect={onMetashotSelected}
          />
          <CategoryFilter
            value={filter?.categoryId as AssetCategoryIdFilter | undefined}
            onSelect={onCategorySelected}
          />
          <LanguageRegionFilter
            value={filter?.languageRegionId as LanguageRegion | undefined}
            onSelect={onRegionLanguageSelected}
          />
          <InputText
            value={filter?.adoptionId?.eq as string}
            width="170"
            prefix="AD-ID-"
            placeholder="Adaption ID"
            onChange={(e) => onAssetAdaptionIdSelected(e.target.value)}
          />
          <InputText
            value={filter?.projectId?.eq as string}
            width="120"
            prefix="P-"
            placeholder="Project ID"
            onChange={(e) => onAssetProjectIdSelected(e.target.value)}
          />
          <InputText
            value={filter?.masterId?.eq as string}
            width="150"
            prefix="MAS-"
            placeholder="Master ID"
            onChange={(e) => onAssetMasterIdSelected(e.target.value)}
          />
          {/* <UISelect className="mr-15" label="Rights" name="rights" /> */}
          <button
            onClick={onResetFilters}
            className="hover:underline inline-block cursor-pointer text-[14px] text-slate-500"
          >
            Reset filter
          </button>
        </div>
      </div>

      <div className="basis-[40%] text-right flex-wrap flex gap-[15px] items-start justify-end">
        <FormControlLabel
          sx={{
            ".MuiFormControlLabel-label": {
              fontSize: "14px",
              whiteSpace: "nowrap",
            },
          }}
          control={
            <Checkbox
              checked={selectedAllAssets}
              onChange={(v) => {
                v.target.checked ? selectAll() : clearSelection();
              }}
            />
          }
          label="Select All"
        />

        <AssetSelectionActions targetAssets={selectedFiles} variant="assets" />

        <Box sx={styles}>
          <FormControl style={{ minWidth: 150 }} size="small">
            <InputLabel className="text-[14px]">Sort by</InputLabel>
            <Select
              className="border-0 rounded-[15px] text-[14px] text-left mr-15"
              label="Sort by"
              value={internalOrderState}
              onChange={handleSortChange}
            >
              <MenuItem value="date_created">Newest</MenuItem>
              <MenuItem value="-date_created">Oldest</MenuItem>
              <MenuItem value="name">Sort A-Z</MenuItem>
              <MenuItem value="-name">Sort Z-A</MenuItem>

              {/*<MenuItem value="-user_created">Last added by all</MenuItem>*/}
              {/* <MenuItem value="category">Category</MenuItem> */}
            </Select>
          </FormControl>
        </Box>
      </div>

      {selectedFiles.length > 0 && (
        <CollectionModal
          openModal={openDialog}
          closeModal={setOpenDialog}
          selectedAssets={selectedFiles}
        />
      )}
    </div>
  );
}
